export {
  type BuilderElement,
  type ElementType,
  type GlobalConfig,
  type Industry,
  type Template,
  type ResponsiveValue,
  generateId,
  validateUniqueIds,
  validateAndFixIds,
  isResponsiveLogo,
  getLogoForBreakpoint,
  normalizeLogoConfig,
  applyLogoToTemplate,
  duplicateElement,
  createNewPopupTemplate,
  addElementToContainer,
  updateElement,
  deleteElement,
  createNewElement,
  findElementById,
  getElementParents,
  hasSelectedChild,
  moveElement,
  reorderElements,
  compileElements,
  stylesToString,
  hoverStylesToString,
  collectCustomFonts,
  updateGeneralSettingsAcrossPages,
  updateGlobalSettingsAcrossPages,
  defaultTemplate,
  getInterfacePageTypesByIndustry,
  TEMPLATE_EXTRA_PAGES,
  getTemplateExtraPages,
} from '@/utils/builder/index';

import {
  template1GlobalConfig,
  template1Template,
  template1VSLTemplate,
} from '@/templates/template1';
import {
  template1Upsell,
  template1UpsellGlobalConfig,
} from '@/templates/template1-upsell';
import { template1Downsell } from '@/templates/template1-downsell';
import {
  template2,
  template2GlobalConfig,
  template2VSLTemplate,
} from '@/templates/template2';
import { template3, template3GlobalConfig } from '@/templates/template3';
import { template4, template4GlobalConfig } from '@/templates/template4';
import {
  template2Upsell,
  template2UpsellGlobalConfig,
} from '@/templates/template2-upsell';
import {
  template2Downsell,
  template2DownsellGlobalConfig,
} from '@/templates/template2-downsell';
import {
  template2Thankyou,
  template2ThankyouGlobalConfig,
} from '@/templates/template2-thankyou';
import {
  template2Contact,
  template2ContactGlobalConfig,
} from '@/templates/template2-contact';
import {
  template2PrivacyPolicy,
  template2PrivacyPolicyGlobalConfig,
} from '@/templates/template2-privacy-policy';
import {
  template2Terms,
  template2TermsGlobalConfig,
} from '@/templates/template2-terms';
import {
  template2Returns,
  template2ReturnsGlobalConfig,
} from '@/templates/template2-returns';
import {
  template2Disclaimer,
  template2DisclaimerGlobalConfig,
} from '@/templates/template2-disclaimer';
import { templateUpsell3 } from '@/templates/templateUpsell3';
import { templateDownsell3 } from '@/templates/templateDownsell3';
import { template3thankyou } from '@/templates/template3thankyou';
import { template3contactUs } from '@/templates/template3contactUs';
import { template3PrivacyPolicy } from '@/templates/template3PrivacyPolicy';
import { template3terms } from '@/templates/template3terms';
import { template3refund } from '@/templates/template3refund';
import { template3disclaimer } from '@/templates/template3disclaimer';
import { template4Upsell } from '@/templates/template4-upsell';
import { template4Downsell } from '@/templates/template4-downsell';
import { template1contactUs } from '@/templates/template1contactUs';
import { template1Thankyou } from '@/templates/template1Thankyou';
import { template1Privacy } from '@/templates/template1Privacy';
import { template1Refund } from '@/templates/template1Refund';
import { template1terms } from '@/templates/template1terms';
import { template1Disclaimer } from '@/templates/template1Disclaimer';
import { VideoPausePopup } from '@/templates/popupTemplates/restaruant-popup-1';
import { FortigoldLabelPopup } from '@/templates/popupTemplates/fortigold-label-popup';
import { KlosLabelPopup } from '@/templates/popupTemplates/klos-label-popup';
import { MovyoLabelPopup } from '@/templates/popupTemplates/movyo-label-popup';
import { createMobileNavigationPopup } from '@/templates/popupTemplates/mobile-navigation-popup';
import { createTemplate2MobileNavigationPopup } from '@/templates/popupTemplates/template2-mobile-navigation-popup';
import { createTemplate3MobileNavigationPopup } from '@/templates/popupTemplates/template3-mobile-navigation-popup';
import { createTemplate4MobileNavigationPopup } from '@/templates/popupTemplates/template4-mobile-navigation-popup';

import type {
  GlobalConfig,
  Industry,
  Template,
  ResponsiveValue,
  BuilderElement,
} from '@/types/builder';
import { generateId, applyLogoToTemplate } from '@/utils/builder/index';

// Synchronous getGlobalConfigByIndustry
export const getGlobalConfigByIndustry = (industry: Industry): GlobalConfig => {
  switch (industry) {
    case 'template1':
      return template1GlobalConfig;
    case 'template2':
      return template2GlobalConfig;
    case 'template3':
      return template3GlobalConfig;
    case 'templateUpsell3':
    case 'templateDownsell3':
      return template3GlobalConfig;
    case 'template4':
    case 'template4-upsell':
    case 'template4-downsell':
      return template4GlobalConfig;
    case 'template2-upsell':
      return template2UpsellGlobalConfig;
    case 'template2-downsell':
      return template2DownsellGlobalConfig;
    case 'template2-thankyou':
      return template2ThankyouGlobalConfig;
    case 'template2-contact':
      return template2ContactGlobalConfig;
    case 'template2-privacy-policy':
      return template2PrivacyPolicyGlobalConfig;
    case 'template2-terms':
      return template2TermsGlobalConfig;
    case 'template2-returns':
      return template2ReturnsGlobalConfig;
    case 'template2-disclaimer':
      return template2DisclaimerGlobalConfig;
    case 'template1-downsell':
    case 'template1-upsell':
      return template1UpsellGlobalConfig;
    case 'blankTemplate':
    default:
      return template1GlobalConfig;
  }
};

// Synchronous getPopupTemplatesByIndustry
export const getPopupTemplatesByIndustry = (
  industry: Industry,
  globalConfig: GlobalConfig
): Template[] => {
  switch (industry) {
    case 'restaurant':
      return [VideoPausePopup];
    case 'template1':
      return [FortigoldLabelPopup, createMobileNavigationPopup(globalConfig)];
    case 'template2':
      return [
        MovyoLabelPopup,
        createTemplate2MobileNavigationPopup(globalConfig),
      ];
    case 'template3':
      return [
        KlosLabelPopup,
        createTemplate3MobileNavigationPopup(globalConfig),
      ];
    case 'template4':
      return [createTemplate4MobileNavigationPopup(globalConfig)];
    default:
      return [];
  }
};

// Helper to create template with logo
const createTemplateWithLogo = (
  templateObj: Template,
  logo?: string | ResponsiveValue<string>,
  businessName?: string
): Template => ({
  ...templateObj,
  elements: applyLogoToTemplate(templateObj.elements, logo, businessName),
});

// Create base template structure
const createBaseTemplate = (
  businessName: string,
  industry: Industry,
  globalConfig: GlobalConfig
): Template => ({
  id: generateId(),
  name: `${businessName} Website`,
  industry,
  elements: [],
  rootContainer: {
    styles: {
      backgroundColor: 'background',
      color: 'forground',
      padding: '0px',
      margin: '0px',
      width: '100%',
      minHeight: '100vh',
      display: 'block',
      position: 'relative',
    },
    responsiveStyles: {},
    hoverStyles: {},
    responsiveHoverStyles: {},
  },
});

// Synchronous getTemplateByIndustry - main template generation
export const getTemplateByIndustry = (
  industry: Industry,
  businessName: string,
  globalConfig: GlobalConfig,
  logoUrl?: string | ResponsiveValue<string>
): Template => {
  const baseTemplate = createBaseTemplate(businessName, industry, globalConfig);

  switch (industry) {
    case 'blankTemplate': {
      baseTemplate.elements = [
        {
          id: generateId(),
          type: 'container',
          styles: {
            backgroundColor: '#FFFFFF',
            padding: '15px 30px',
            display: 'flex',
            justifyContent: 'center',
          },
          children: [
            {
              id: generateId(),
              type: 'heading',
              content: 'Welcome to LandingPage Builder',
              styles: {
                fontSize: '24px',
                fontWeight: 'bold',
                color: globalConfig.colors.primary,
                margin: '0',
              },
            },
          ],
        },
      ];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1': {
      const t = template1Template(globalConfig);
      baseTemplate.elements = [
        t.header,
        t.firstSection,
        t.fortigoldFeatureSection,
        t.healthSolutionSection,
        t.fortigoldWorksSection,
        t.fortigoldGutHealthSection,
        t.fortigoldTestimonialsSection,
        t.fortigoldLimitedSupplySection,
        t.pricingSection1,
        t.fortigoldGuaranteeCertificateSection,
        t.vipBonusesSection,
        t.fortigoldFAQSection,
        t.fortigoldLimitedSupplySection1,
        t.pricingSection,
        t.scientificReferencesSection,
        t.scientificRefListingSection,
        t.footerMenuSection,
        t.footerEndSection,
      ].filter(Boolean) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1VSL': {
      const t = template1VSLTemplate(globalConfig);
      baseTemplate.elements = [
        t.header,
        t.embededVideo,
        t.footerMenuSection,
        t.footerEndSection,
      ].filter(Boolean) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2': {
      const t = template2(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2VSL': {
      const t = template2VSLTemplate(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template3': {
      const t = template3(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template4': {
      const t = template4(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1-upsell': {
      const t = template1Upsell(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1-downsell': {
      const t = template1Downsell(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2-upsell': {
      const t = template2Upsell(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2-downsell': {
      const t = template2Downsell(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2-thankyou': {
      const t = template2Thankyou(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2-contact': {
      const t = template2Contact(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2-privacy-policy': {
      const t = template2PrivacyPolicy(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2-terms': {
      const t = template2Terms(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2-returns': {
      const t = template2Returns(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template2-disclaimer': {
      const t = template2Disclaimer(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'templateUpsell3': {
      const t = templateUpsell3(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'templateDownsell3': {
      const t = templateDownsell3(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template3thankyou': {
      const t = template3thankyou(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template3contactUs': {
      const t = template3contactUs(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template3PrivacyPolicy': {
      const t = template3PrivacyPolicy(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template3terms': {
      const t = template3terms(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template3refund': {
      const t = template3refund(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template3disclaimer': {
      const t = template3disclaimer(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template4-upsell': {
      const t = template4Upsell(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template4-downsell': {
      const t = template4Downsell(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1contactUs': {
      const t = template1contactUs(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1Thankyou': {
      const t = template1Thankyou(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1Privacy': {
      const t = template1Privacy(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1Refund': {
      const t = template1Refund(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1terms': {
      const t = template1terms(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    case 'template1Disclaimer': {
      const t = template1Disclaimer(globalConfig);
      baseTemplate.elements = Object.values(t).filter(
        Boolean
      ) as unknown as BuilderElement[];
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
    }

    default:
      return createTemplateWithLogo(baseTemplate, logoUrl, businessName);
  }
};
